import { useState, useEffect } from "react";
import { businessApi } from "@/api/businessApi";
import { FiGlobe, FiMail, FiPhone } from "react-icons/fi";
import { IoBusinessOutline } from "react-icons/io5";
import {
  FaMapMarkerAlt,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import BusinessHoursDisplay from "./BusinessHoursDisplay";
import EditBusinessModal from "./EditBusinessModal";

const MyBusiness = () => {
  const [businessData, setBusinessData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Helper function to format URLs with proper protocol
  const formatUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `https://${url}`;
  };

  const fetchBusinessData = async () => {
    try {
      const response = await businessApi.getBusinessData();
      setBusinessData(response.data);
    } catch (error) {
      console.error("Error fetching business data:", error);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await fetchBusinessData();
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleBusinessUpdate = async (updatedData: any) => {
    // First update with the response data
    setBusinessData(updatedData);
    // Then refetch from API to ensure UI reflects server state
    await fetchBusinessData();
  };

  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto">
        <div className="mx-auto py-8 px-8">
          <div className="animate-pulse">
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-3 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto py-8 px-8">
        {/* Business Header */}
        <div className="flex items-center justify-between space-x-4 mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-[#78C7D1] rounded-full flex items-center justify-center text-white text-xl">
              {businessData?.business_name?.charAt(0) || "B"}
            </div>
            <div>
              <h2 className="text-lg font-medium">
                {businessData?.business_name}
              </h2>
              <p className="text-sm text-gray-500">
                {businessData?.address?.city}, {businessData?.address?.state}
              </p>
            </div>
          </div>
          {/* Edit Button */}
          <button
            className="ml-auto p-2 hover:bg-gray-100 rounded-full"
            onClick={() => setIsEditModalOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </button>
        </div>

        {/* Business Information */}
        <div className="grid grid-cols-1 gap-6">
          {/* Contact Information */}
          <div className="space-y-4">
            {businessData?.phones?.length > 0 && (
              <div className="flex items-center space-x-3">
                <FiPhone className="text-gray-400" />
                <span>{businessData.phones[0]}</span>
              </div>
            )}

            {businessData?.emails?.length > 0 && (
              <div className="flex items-center space-x-3">
                <FiMail className="text-gray-400" />
                <span>{businessData.emails[0]}</span>
              </div>
            )}

            {businessData?.business_name && (
              <div className="flex items-center space-x-3">
                <IoBusinessOutline className="text-gray-400" />
                <span>{businessData.business_name}</span>
              </div>
            )}

            {businessData?.address && (
              <div className="flex items-center space-x-3">
                <FaMapMarkerAlt className="text-gray-400" />
                <span>
                  {businessData.address.line_1}
                  {businessData.address.line_2
                    ? `, ${businessData.address.line_2}`
                    : ""}
                  ,{businessData.address.city}, {businessData.address.state}{" "}
                  {businessData.address.zip_code}
                </span>
              </div>
            )}

            {businessData?.social_links?.website && (
              <div className="flex items-center space-x-3">
                <FiGlobe className="text-gray-400" />
                <a
                  href={formatUrl(businessData.social_links.website)}
                  className="text-[#78C7D1] hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {businessData.social_links.website}
                </a>
              </div>
            )}

            {businessData?.social_links?.facebook && (
              <div className="flex items-center space-x-3">
                <FaFacebook className="text-gray-400" />
                <a
                  href={formatUrl(businessData.social_links.facebook)}
                  className="text-[#78C7D1] hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
              </div>
            )}

            {businessData?.social_links?.twitter && (
              <div className="flex items-center space-x-3">
                <FaTwitter className="text-gray-400" />
                <a
                  href={formatUrl(businessData.social_links.twitter)}
                  className="text-[#78C7D1] hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Twitter
                </a>
              </div>
            )}

            {businessData?.social_links?.linkedin && (
              <div className="flex items-center space-x-3">
                <FaLinkedin className="text-gray-400" />
                <a
                  href={formatUrl(businessData.social_links.linkedin)}
                  className="text-[#78C7D1] hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </div>
            )}

            {businessData?.social_links?.instagram && (
              <div className="flex items-center space-x-3">
                <FaInstagram className="text-gray-400" />
                <a
                  href={formatUrl(businessData.social_links.instagram)}
                  className="text-[#78C7D1] hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram
                </a>
              </div>
            )}

            {businessData?.social_links?.youtube && (
              <div className="flex items-center space-x-3">
                <FaYoutube className="text-gray-400" />
                <a
                  href={formatUrl(businessData.social_links.youtube)}
                  className="text-[#78C7D1] hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  YouTube
                </a>
              </div>
            )}

            {businessData?.working_hours && (
              <BusinessHoursDisplay workingHours={businessData.working_hours} />
            )}
          </div>
        </div>
      </div>
      {isEditModalOpen && (
        <EditBusinessModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          businessData={businessData}
          onUpdate={handleBusinessUpdate}
        />
      )}
    </div>
  );
};

export default MyBusiness;
